<template>
  <div class="comic-translate-container">
    <!-- 背景层 -->
    <div class="bg-layer">
      <div class="bg-image"></div>
      <div class="bg-gradient"></div>
    </div>

    <!-- 导航栏 -->
    <ComicNavbar />

    <!-- 粒子效果 -->
    <Particles />

    <!-- 主内容区域 -->
    <div class="main-content">
      <div class="container">
        <h1 class="page-title">{{ $t('translate.pageTitle') }}<span class="preview-icon-wrap"><span class="preview-icon" @click="showPreviewDialog" :title="$t('translate.previewDialog')"><svg viewBox="0 0 48 36" width="48" height="36" fill="currentColor" stroke="none"><path d="M24 8l2.5 9.5L36 19l-9.5 1.5L24 30l-2.5-9.5L12 19l9.5-1.5z"/><path class="star-sm star-s1" d="M8 6l1.2 4.5L14 11.7l-4.8.8L8 17l-1.2-4.5L2 11.7l4.8-.8z"/><path class="star-sm star-s2" d="M40 4l1 3.5L48 8.5l-7 .6L40 13l-1-3.9L32 8.5l7-.6z"/><path class="star-sm star-s3" d="M6 24l.8 3L12 28l-5.2.4L6 32l-.8-3.6L0 28l5.2-.4z"/><path class="star-sm star-s4" d="M42 26l.8 3L48 30l-5.2.4L42 34l-.8-3.6L36 30l5.2-.4z"/></svg></span><span v-if="showGuideArrow" class="guide-arrow"><span class="guide-text">{{ $t('translate.guideArrow') }}</span><svg viewBox="0 0 32 28" width="24" height="21" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M16 26V8"/><path d="M8 16l8-8 8 8"/><circle cx="16" cy="26" r="2" fill="currentColor" stroke="none"/></svg></span></span></h1>
        <p class="page-desc">{{ $t('translate.pageDesc') }}</p>

        <!-- 队列状态卡片 -->
        <QueueCard :queue-count="queueCount" />

        <!-- 上传和预览区域 -->
        <div class="upload-preview-row">
          <!-- 上传区域 -->
          <div class="card upload-card">
            <h3 class="card-title"><i class="fas fa-cloud-upload-alt"></i> {{ $t('translate.uploadTitle') }}</h3>
            <LeftTop class="upload-component" @file-change="handleFileChange" :is-translating="isTranslating"
              ref="leftTopRef" />
          </div>

          <!-- 预览区域 -->
          <div class="card preview-card">
            <h3 class="card-title"><i class="fas fa-eye"></i> {{ $t('translate.previewTitle') }}</h3>
            <FreeRightPanel ref="rightPanelRef" :uploaded-image="uploadedImage" :temp-file="tempFile"
              :queue-count="queueCount" :is-translating="isTranslating" :on-file-added="handleFileAdded"
              :on-translation-status-change="handleTranslationStatusChange" :uploaded-images="uploadedImages"
              :all-files="allFiles" :current-index="currentImageIndex" @file-change="handleFileChange"
              @select-image="handleSelectImage" @clear-all="handleClearAll" @open-config="openConfigDialog"
              class="preview-component" />
          </div>
        </div>

        <!-- 翻译历史列表 -->
        <div class="translate-history-section">
          <div class="translate-list">
            <div class="list-header">
              <h3 class="card-title"><i class="fas fa-list"></i> {{ $t('translate.historyTitle') }}</h3>
              <div class="header-actions">
                <div class="batch-download-wrap">
                  <el-button class="batch-download-btn" type="primary" plain @click="handleBatchDownload"
                    :disabled="selectedFileIds.length === 0 || isBatchDownloading">
                    {{ isBatchDownloading ? $t('translate.downloading') : $t('translate.batchDownload') }} ({{ selectedFileIds.length }})
                  </el-button>
                  <div v-if="isBatchDownloading" class="download-progress-bar">
                    <div class="download-progress-fill" :style="{ width: downloadProgress + '%' }"></div>
                    <span class="download-progress-text">{{ downloadProgress }}%</span>
                  </div>
                </div>
                <el-button class="clear-btn" @click="clearAll" type="primary" plain>{{ $t('translate.clearList') }}</el-button>
              </div>
            </div>
            <LeftBottom :file-list="fileList" @preview="previewFile" @download="downloadFile" @delete="deleteFile"
              @clearAll="clearAll" @selection-change="handleSelectionChange" />
          </div>
        </div>
      </div>
    </div>

    <!-- 清除浮动 -->
    <div style="clear: both;"></div>

    <!-- 底部 -->
    <ComicFooter @show-modal="showModal" />

    <!-- 效果预览弹窗 -->
    <Teleport to="body">
      <div v-if="previewDialogVisible" class="preview-dialog-overlay" @click.self="closePreviewDialog">
        <div class="preview-dialog">
          <div class="preview-dialog-close" @click="closePreviewDialog">
            <i class="fas fa-times"></i>
          </div>
          <h3 class="preview-dialog-title">{{ $t('translate.previewDialog') }}</h3>
          <div class="preview-dialog-body">
            <div class="preview-dialog-side">
              <img :src="previewImgA" :alt="$t('translate.previewDialog')" />
            </div>
            <div class="preview-dialog-side">
              <img :src="previewImgB" :alt="$t('translate.previewDialog')" />
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 蒙版预览 -->
    <Teleport to="body">
      <div v-if="previewMaskVisible" class="mask-preview-container" @click="closePreviewMask">
        <img :src="previewImageUrl" class="mask-preview-image" @click.stop />
      </div>
    </Teleport>

    <!-- 弹窗 -->
    <ComicModal :is-visible="modalVisible" :title="modalTitle" :modal-type="modalType" @close="closeModal" />

    <!-- 翻译配置弹窗 -->
    <ComicModal :is-visible="configDialogVisible" :title="$t('manga.vip.configTitle')" modal-type="config"
      @close="configDialogVisible = false">
      <div class="config-form">
        <label class="config-label">{{ $t('manga.vip.configLangLabel') }}</label>
        <el-select v-model="targetLanguage" :placeholder="$t('manga.vip.configLangPlaceholder')" style="width: 100%;"
          popper-class="config-select-popper">
          <el-option v-for="item in languageOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </div>
      <template #footer>
        <div class="config-footer">
          <el-button type="primary" @click="handleConfigConfirm">{{ $t('manga.vip.configConfirm') }}</el-button>
        </div>
      </template>
    </ComicModal>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick, provide } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import LeftTop from '@/components/manga/LeftTop.vue'
import LeftBottom from '@/components/manga/LeftBottom.vue'
import FreeRightPanel from '@/components/manga/FreeRightPanel.vue'
import ComicNavbar from '@/components/manga/ComicNavbar.vue'
import Particles from '@/components/Particles.vue'
import ComicFooter from '@/components/manga/ComicFooter.vue'
import ComicModal from '@/components/manga/ComicModal.vue'
import QueueCard from '@/components/manga/QueueCard.vue'
import previewImgA from '@/assets/images/yl-a.jpg'
import previewImgB from '@/assets/images/yl-b.jpg'

// 导入API接口
import { downloadManga, batchDownloadManga } from '@/api/upload.js'
import { getMangaInfo, getTeam } from '@/api/manga/manga.js'
import { getDictItemsByDictType } from '@/api/admin/dictItem.js'

// 导入上传接口（需要用于provide）
import { uploadMultipleManga } from '@/api/upload.js'

// 导入SEO工具函数

const { t } = useI18n()

// ====================== 存储操作工具函数 ======================
// 使用 localStorage 替代 Cookie，避免大小限制问题
const storage = {
  set: (name, value) => {
    try {
      const data = typeof value === 'object' ? JSON.stringify(value) : value
      localStorage.setItem(name, data)
      console.log(`[存储] 已保存到localStorage: ${name}, 大小: ${data.length}字符`)
    } catch (error) {
      console.error(`[存储错误] 保存到localStorage失败:`, error)
    }
  },

  get: (name) => {
    try {
      const data = localStorage.getItem(name)
      if (data) {
        console.log(`[存储] 从localStorage读取: ${name}`)
        try {
          return JSON.parse(data)
        } catch (e) {
          return data
        }
      }
      console.log(`[存储] localStorage中未找到: ${name}`)
      return null
    } catch (error) {
      console.error(`[存储错误] 从localStorage读取失败:`, error)
      return null
    }
  },

  remove: (name) => {
    try {
      localStorage.removeItem(name)
      console.log(`[存储] 已从localStorage删除: ${name}`)
    } catch (error) {
      console.error(`[存储错误] 从localStorage删除失败:`, error)
    }
  }
}

// 为了保持兼容性，保留原有的函数名
const setCookie = storage.set
const getCookie = (name) => storage.get(name)
const deleteCookie = storage.remove

// 格式化错误消息
const formatErrorMessages = (messages) => {
  return messages.map((msg, index) => `${index + 1}. ${msg}`).join('\n\n')
}

// 显示批量上传结果弹窗
const showBatchResultDialog = (content, title, type = 'warning') => {
  return ElMessageBox.alert(content, title, {
    confirmButtonText: t('translate.confirm'),
    type,
    showClose: false,
    closeOnClickModal: false,
    closeOnPressEscape: false,
    customClass: 'batch-upload-result-dialog',
    customStyle: { 'max-width': '800px', 'width': '90%' }
  })
}

// 显示确认对话框
const showConfirmDialog = (message, title) => {
  return ElMessageBox.confirm(message, title, {
    confirmButtonText: t('translate.confirm'),
    cancelButtonText: t('translate.cancel'),
    type: 'warning',
    confirmButtonClass: 'confirm-delete-btn'
  })
}

// ====================== 核心数据定义 ======================
const defaultFileList = []

const tempFile = ref(null)
const uploadedImage = ref('')
const uploadedImages = ref([])
const allFiles = ref([])
const currentImageIndex = ref(0)
const currentImgId = ref('')

const fileList = ref(storage.get('imageTranslatorFileList') || defaultFileList)
const isTranslating = ref(false)
const statusCheckTimer = ref(null)
const queueCount = ref(0)
const queueCheckTimer = ref(null)
const rightPanelRef = ref(null)
const leftTopRef = ref(null)
const selectedFileIds = ref([]) // 存储选中的文件imgId
const isBatchDownloading = ref(false) // 批量下载状态控制
const downloadProgress = ref(0) // 批量下载进度 0-100

// 效果预览弹窗
const previewDialogVisible = ref(false)
const showPreviewDialog = () => {
  previewDialogVisible.value = true
  dismissGuideArrow()
}
const closePreviewDialog = () => {
  previewDialogVisible.value = false
}

// 新手引导箭头
const showGuideArrow = ref(!storage.get('previewGuideShown'))
const dismissGuideArrow = () => {
  if (showGuideArrow.value) {
    showGuideArrow.value = false
    storage.set('previewGuideShown', true)
  }
}

// 蒙版预览相关变量
const previewMaskVisible = ref(false) // 蒙版显隐控制
const previewImageUrl = ref('') // 预览图片地址

// 计算已完成的数量
const completedCount = computed(() => {
  return fileList.value.filter((file) => file.status === '已完成').length
})


// 确保在组件挂载时正确加载Cookie数据
onMounted(() => {
  // 验证cookie数据的有效性
  try {
    const savedFileList = getCookie('imageTranslatorFileList')
    if (savedFileList && Array.isArray(savedFileList) && savedFileList.length > 0) {
      // 确保每个文件对象都有必要的字段
      const validFiles = savedFileList.filter(file =>
        file && (file.id || file.imgId) && file.fileName
      )
      if (validFiles.length !== fileList.value.length) {
        console.warn('Cookie数据验证失败，重新加载有效数据')
        fileList.value = validFiles
      }
    }
  } catch (error) {
    console.error('验证Cookie数据失败:', error)
  }

  updateFileStatus()
  fetchQueueCount()
  fetchLanguageOptions()
  statusCheckTimer.value = setInterval(updateFileStatus, 30000)
  queueCheckTimer.value = setInterval(fetchQueueCount, 30000)
  // 添加ESC按键监听
  document.addEventListener('keydown', handleEscKey)
})

// ====================== 获取排队人数方法 ======================
const fetchQueueCount = async () => {
  try {
    const { data } = await getTeam()
    queueCount.value = Number(data) || 0
  } catch (error) {
    console.error('获取排队人数失败：', error)
  }
}

// ====================== 查询并更新文件状态 ======================
const updateFileStatus = async () => {
  const needCheckFiles = fileList.value.filter((file) => file.imgId && ['翻译中', '已完成'].includes(file.status))
  if (needCheckFiles.length === 0) return

  try {
    const imgIds = needCheckFiles.map((file) => file.imgId)
    if (imgIds.length === 0) return
    const data = await getMangaInfo({ ids: imgIds })
    const statusMap = data.data

    let hasChanges = false
    fileList.value = fileList.value.map((file) => {
      if (statusMap[file.imgId] && file.status !== statusMap[file.imgId]) {
        hasChanges = true
        return { ...file, status: statusMap[file.imgId] }
      }
      return file
    })

    // 如果有状态变化，直接保存到缓存
    if (hasChanges) {
      storage.set('imageTranslatorFileList', fileList.value)
    }
  } catch (error) {
    console.error('更新文件状态失败：', error)
  }
}

// ====================== 预览文件方法 ======================
const previewFile = (file) => {
  if (!file.imgId) {
    ElMessage.error(t('translate.fileIdMissing'))
    return
  }
  // 拼接预览地址
  const ext = file.fileName.substring(file.fileName.lastIndexOf('.'))
  previewImageUrl.value = `${window.global_config.previewId}/${file.imgId}${ext}`
  // 显示蒙版预览
  previewMaskVisible.value = true
}

// ====================== 关闭蒙版预览方法 ======================
const closePreviewMask = () => {
  previewMaskVisible.value = false
  previewImageUrl.value = ''
}

// ====================== ESC按键监听方法 ======================
const handleEscKey = (e) => {
  if (e.key === 'Escape' && previewMaskVisible.value) {
    closePreviewMask()
  }
}

// ====================== 文件变更处理 ======================
const handleFileChange = ({ file, imageUrl, allFiles: files, allImages, clearAll }) => {
  tempFile.value = file
  uploadedImage.value = imageUrl

  // 如果是清空操作，重置所有状态
  if (clearAll) {
    uploadedImages.value = []
    allFiles.value = []
    currentImageIndex.value = 0
    return
  }

  uploadedImages.value = allImages || []
  allFiles.value = files || []
  if (allImages && allImages.length > 0) {
    currentImageIndex.value = allImages.length - 1
  } else {
    currentImageIndex.value = 0
  }

  // 当文件被选择时，触发队列计数更新
  if (files && files.length > 0) {
    fetchQueueCount()
  }
}


// ====================== 文件添加处理 ======================
const handleFileAdded = (newFile) => {
  console.log('[缓存操作] 开始添加文件到缓存，新文件:', newFile)
  fileList.value.unshift(newFile)

  // 直接保存到localStorage
  console.log('[缓存操作] 正在保存到localStorage，当前fileList长度:', fileList.value.length)
  storage.set('imageTranslatorFileList', fileList.value)
  console.log('[缓存操作] 已保存到localStorage完成')

  setTimeout(() => updateFileStatus(), 1000)
  fetchQueueCount()
}

// ====================== 选择图片处理 ======================
const handleSelectImage = ({ index, imageUrl }) => {
  currentImageIndex.value = index
  uploadedImage.value = imageUrl
}


// ====================== 翻译状态变更处理 ======================
const handleTranslationStatusChange = (translating) => {
  isTranslating.value = translating
}

// ====================== 清空图片预览 ======================
const clearImagePreview = () => {
  uploadedImage.value = ''
  tempFile.value = null
  currentImgId.value = ''

  // 清空预览蒙版状态
  previewMaskVisible.value = false
  previewImageUrl.value = ''

  // 同步清空右侧面板的预览
  if (rightPanelRef.value && typeof rightPanelRef.value.clearAllImages === 'function') {
    rightPanelRef.value.clearAllImages()
  }
}



// ====================== 清除所有图片 ======================
const handleClearAll = () => {
  // 清空所有相关状态，确保第二次上传时状态正确
  uploadedImages.value = []
  allFiles.value = []
  currentImageIndex.value = 0
  clearImagePreview()

  // 同步清空左侧上传组件的状态
  if (leftTopRef.value && typeof leftTopRef.value.clearAllFiles === 'function') {
    leftTopRef.value.clearAllFiles()
  }
}

// ====================== 下载文件功能 ======================
const downloadFile = async (file) => {
  if (file.status !== '已完成') {
    ElMessage.error(t('translate.fileNotComplete'))
    return
  }
  if (!file.imgId) {
    ElMessage.error(t('translate.fileIdMissing'))
    return
  }

  try {
    const response = await downloadManga(file.imgId)
    const blob = new Blob([response.data])
    let fileName = file.fileName
    const contentDisposition = response.headers['content-disposition']
    if (contentDisposition) {
      const matches = /filename="?([^";]+)"?/.exec(contentDisposition)
      if (matches && matches[1]) {
        fileName = decodeURIComponent(matches[1])
      }
    }

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    ElMessage.success(`${t('translate.downloadSuccess')}: ${fileName}`)
  } catch (error) {
    console.error('下载失败详情：', error)
    if (error.response) {
      if (error.response.status === 404) {
        ElMessage.error(t('translate.fileNotFound'))
        // 优先使用imgId查找，如果没有则使用id作为后备
        let fileItem = fileList.value.find((item) => item.imgId === file.imgId)
        if (!fileItem) {
          fileItem = fileList.value.find((item) => item.id === file.id)
        }
        if (fileItem) {
          fileItem.status = '失败'
        }
      } else if (error.response.status === 500) {
        ElMessage.error(t('translate.serverError'))
      } else {
        ElMessage.error(`${t('translate.downloadFailed')}: HTTP ${error.response.status}`)
      }
    } else if (error.request) {
      ElMessage.error(t('translate.networkError'))
    } else {
      ElMessage.warning(t('translate.noFileFound'))
    }
  }
}

// ====================== 删除文件功能 ======================
const deleteFile = async (file) => {
  showConfirmDialog(t('translate.deleteConfirm'), t('translate.deleteTitle')).then(async () => {
    try {
      console.log('[缓存操作] 开始删除文件:', file)
      fileList.value = fileList.value.filter((item) => item.id !== file.id)

      // 直接保存到localStorage
      console.log('[缓存操作] 正在保存删除后的fileList到localStorage，剩余文件数:', fileList.value.length)
      storage.set('imageTranslatorFileList', fileList.value)
      console.log('[缓存操作] 删除文件并保存到localStorage完成')

      // 同时从选中的imgId中移除
      if (file.imgId) {
        selectedFileIds.value = selectedFileIds.value.filter(imgId => imgId !== file.imgId)
      }
      ElMessage.success(t('translate.deleteSuccess'))
    } catch (error) {
      console.error('删除文件失败：', error)
      ElMessage.error(`${t('translate.deleteFailed')}: ${error.message || '清除缓存失败'}`)
    }
  })
}

// ====================== 处理选择状态变化 ======================
const handleSelectionChange = (selectedIds) => {
  selectedFileIds.value = selectedIds
}

// ====================== 批量下载功能 ======================
const handleBatchDownload = async () => {
  if (selectedFileIds.value.length === 0) {
    ElMessage.warning(t('translate.selectFilesFirst'))
    return
  }

  // 检查选中的文件是否都是已完成的
  const selectedFiles = fileList.value.filter(file => selectedFileIds.value.includes(file.imgId))
  const incompleteFiles = selectedFiles.filter(file => file.status !== '已完成')

  // 检查是否有有效的imgId
  const invalidFiles = selectedFiles.filter(file => !file.imgId || file.imgId.trim() === '')
  if (invalidFiles.length > 0) {
    ElMessage.error(t('translate.invalidFiles', { count: invalidFiles.length }))
    return
  }

  // 调试信息：显示选中的文件详情

  if (incompleteFiles.length > 0) {
    ElMessage.warning(t('translate.incompleteFiles', { count: incompleteFiles.length }))
    return
  }

  // 如果正在下载中，直接返回，防止重复操作
  if (isBatchDownloading.value) {
    ElMessage.warning(t('translate.downloadingWait'))
    return
  }

  try {
    // 设置下载状态为true，禁用按钮
    isBatchDownloading.value = true
    downloadProgress.value = 0
    ElMessage.success(t('translate.batchDownloadStart', { count: selectedFileIds.value.length }))
    const response = await batchDownloadManga(selectedFileIds.value, undefined, (progress) => {
      downloadProgress.value = progress
    })

    const blob = new Blob([response.data])

    // 生成下载文件名
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
    const fileName = `${t('translate.batchDownloadFilename')}_${timestamp}.zip`

    // 创建下载链接
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()

    // 清理
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    ElMessage.success(`${t('translate.batchDownloadSuccess')}: ${fileName}`)
  } catch (error) {
    console.error('批量下载失败详情：', {
      error: error,
      message: error.message,
      response: error.response,
      request: error.request,
      selectedIds: selectedFileIds.value
    })

    if (error.response) {
      console.error('服务器响应错误:', error.response)
      if (error.response.status === 400) {
        ElMessage.error(t('translate.paramError'))
      } else if (error.response.status === 500) {
        const errorMessage = error.response.data ?
          (typeof error.response.data === 'string' ? error.response.data : t('translate.serverError')) :
          t('translate.serverError')
        ElMessage.error(errorMessage)
      } else if (error.response.data) {
        try {
          const errorText = typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data)
          ElMessage.error(`${t('translate.batchDownloadFailed')}: ${errorText}`)
        } catch (e) {
          ElMessage.error(`${t('translate.batchDownloadFailed')}: HTTP ${error.response.status}`)
        }
      } else {
        ElMessage.error(`${t('translate.batchDownloadFailed')}: HTTP ${error.response.status}`)
      }
    } else if (error.request) {
      ElMessage.error(t('translate.networkError'))
    } else {
      ElMessage.error(`${t('translate.batchDownloadFailed')}: ${error.message || '未知错误'}`)
    }
  } finally {
    // 无论成功还是失败，都要重置下载状态，启用按钮
    isBatchDownloading.value = false
    downloadProgress.value = 0
  }
}



// ====================== 清空列表功能 ======================
const clearAll = () => {
  showConfirmDialog(t('translate.clearConfirm'), t('translate.clearTitle')).then(async () => {
    try {
      console.log('[缓存操作] 开始清空所有文件')
      fileList.value = []

      // 直接清除localStorage
      console.log('[缓存操作] 正在清除localStorage')
      storage.remove('imageTranslatorFileList')
      console.log('[缓存操作] 清空所有文件完成')

      selectedFileIds.value = [] // 清空选中的imgId
      clearImagePreview()
      ElMessage.success(t('translate.clearSuccess'))
    } catch (error) {
      console.error('清空列表失败：', error)
      ElMessage.error(`${t('translate.clearFailed')}: ${error.message || '清除缓存失败'}`)
    }
  })
}

// 弹窗状态管理
const modalVisible = ref(false)
const modalType = ref('')
const modalTitle = ref('')

const showModal = (type) => {
  modalType.value = type
  switch (type) {
    case 'privacy':
      modalTitle.value = t('translate.privacy')
      break
    case 'agreement':
      modalTitle.value = t('translate.agreement')
      break
    case 'about':
      modalTitle.value = t('translate.about')
      break
  }
  modalVisible.value = true
}

const closeModal = () => {
  modalVisible.value = false
  modalType.value = ''
  modalTitle.value = ''
}

// ====================== 翻译配置（免费通道，仅目标语言） ======================
const configDialogVisible = ref(false)
const targetLanguage = ref(localStorage.getItem('freeTargetLanguage') || 'CHS')
const languageOptions = ref([])

const fetchLanguageOptions = async () => {
  try {
    const res = await getDictItemsByDictType('target_language')
    if (res.code === 200 && res.data) {
      languageOptions.value = res.data.map(item => ({
        label: item.dictLabel,
        value: item.dictValue
      }))
      if (!targetLanguage.value || !languageOptions.value.some(item => item.value === targetLanguage.value)) {
        targetLanguage.value = 'CHS'
      }
    }
  } catch (error) {
    console.error('获取目标语言字典失败：', error)
  }
}

const openConfigDialog = () => {
  targetLanguage.value = localStorage.getItem('freeTargetLanguage') || 'CHS'
  configDialogVisible.value = true
}

const handleConfigConfirm = () => {
  localStorage.setItem('freeTargetLanguage', targetLanguage.value)
  configDialogVisible.value = false
  ElMessage.success('已保存翻译配置')
}

// 提供API方法给子组件
provide('uploadMultipleManga', uploadMultipleManga)



// 验证存储数据的有效性
try {
  const savedFileList = storage.get('imageTranslatorFileList')
  if (savedFileList && Array.isArray(savedFileList) && savedFileList.length > 0) {
    // 确保每个文件对象都有必要的字段
    const validFiles = savedFileList.filter(file =>
      file && (file.id || file.imgId) && file.fileName
    )

    if (validFiles.length !== fileList.value.length) {
      console.warn('[存储] 数据验证失败，重新加载有效数据')
      fileList.value = validFiles
    }
  }
} catch (error) {
  console.error('[存储] 验证数据失败:', error)
  updateFileStatus()
  fetchQueueCount()
  statusCheckTimer.value = setInterval(updateFileStatus, 30000)
  queueCheckTimer.value = setInterval(fetchQueueCount, 30000)
  // 添加ESC按键监听
  document.addEventListener('keydown', handleEscKey)
}


onUnmounted(() => {
  if (statusCheckTimer.value) {
    clearInterval(statusCheckTimer.value)
  }
  if (queueCheckTimer.value) {
    clearInterval(queueCheckTimer.value)
  }
  // 移除ESC按键监听
  document.removeEventListener('keydown', handleEscKey)
})
</script>

<style scoped>
.comic-translate-container {
  min-height: 100vh;
  position: relative;
  color: #f1f5f9;
  overflow-x: hidden;
  background: #0a0f1a;
}

.bg-layer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.bg-layer .bg-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url('~@/assets/images/login.jpg') no-repeat center center;
  background-size: cover;
}

.bg-layer .bg-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(10, 15, 26, 0.92) 0%, rgba(15, 23, 42, 0.88) 50%, rgba(30, 41, 59, 0.85) 100%);
}

.comic-translate-container > *:not(.bg-layer):not(.bg-particles):not(.navbar) {
  position: relative;
  z-index: 1;
}

.bg-particles {
  z-index: 1;
}

.main-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 5rem 2rem 0;
  width: 100%;
  box-sizing: border-box;
}

.page-title {
  text-align: center;
  margin-bottom: 1rem;
  font-size: 2rem;
  background: linear-gradient(90deg, #5eead4, #a78bfa);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-desc {
  text-align: center;
  color: #94a3b8;
  margin-bottom: 2rem;
  font-size: 1rem;
}

/* 功能布局 */
.upload-preview-row {
  display: flex;
  /* grid-template-columns: 1fr 1fr; */
  gap: 2rem;
  margin-bottom: 2rem;
  align-items: stretch;
  /* 让两个卡片拉伸到相同高度 */
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* 通用卡片样式 */
.card {
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(94, 234, 212, 0.1);
  border-radius: 16px;
  padding: 1.8rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  /* 让卡片高度自动填充父容器 */
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 50%;
  box-sizing: border-box;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 40px rgba(94, 234, 212, 0.1);
  border-color: rgba(94, 234, 212, 0.3);
}

.card-title {
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  color: #e2e8f0;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 0px;
}

.card-title i {
  color: #5eead4;
}

/* 上传和预览组件样式 */
.upload-component {
  flex: 1;
  /* 让组件填充卡片剩余空间 */
  display: flex;
  flex-direction: column;
}

.preview-component {
  flex: 1;
  /* 让组件填充卡片剩余空间 */
  display: flex;
  flex-direction: column;
}

/* 翻译历史区域 */
.translate-history-section {
  margin-bottom: 5rem;
  /* 为底部菜单留出空间 */
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* 翻译列表样式 */
.translate-list {
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(94, 234, 212, 0.1);
  border-radius: 16px;
  padding: 1.8rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  /* 让卡片高度自动填充父容器 */
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.header-actions {
  display: flex;
  gap: 10px;
}

:deep(.batch-download-btn.el-button--primary.is-plain) {
  background: rgba(94, 234, 212, 0.1) !important;
  color: #5eead4 !important;
  border: 1px solid rgba(94, 234, 212, 0.2) !important;
  border-radius: 6px !important;
  transition: all 0.3s ease !important;
}

:deep(.batch-download-btn.el-button--primary.is-plain:hover) {
  background: rgba(94, 234, 212, 0.2) !important;
}

.batch-download-wrap {
  position: relative;
}

.download-progress-bar {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 160px;
  height: 24px;
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(94, 234, 212, 0.3);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 6px;
  pointer-events: none;
}

.download-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, rgba(94, 234, 212, 0.4), rgba(94, 234, 212, 0.7));
  border-radius: 12px;
  transition: width 0.3s ease;
}

.download-progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 11px;
  color: #fff;
  font-weight: 500;
}

:deep(.clear-btn.el-button--primary.is-plain) {
  background: rgba(94, 234, 212, 0.1) !important;
  color: #5eead4 !important;
  border: 1px solid rgba(94, 234, 212, 0.2) !important;
  border-radius: 6px !important;
  transition: all 0.3s ease !important;
}

:deep(.clear-btn.el-button--primary.is-plain:hover) {
  background: rgba(94, 234, 212, 0.2) !important;
}

/* 蒙版预览样式优化 */
.mask-preview-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.9);
  backdrop-filter: blur(8px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 20px;
  box-sizing: border-box;
}

.mask-preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  cursor: default;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

/* 响应式 */
@media (max-width: 900px) {
  .upload-preview-row {
    flex-direction: column;
    gap: 1.5rem;
  }

  .card {
    max-width: 100%;
  }

  .main-content {
    padding: 5rem 1rem 6rem;
  }

  .container {
    gap: 1.5rem;
  }
}

@media (max-width: 500px) {
  .page-title {
    font-size: 1.5rem;
  }

  .card {
    padding: 1.2rem;
  }

  .list-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
}

/* 效果预览图标 */
.preview-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 12px;
  cursor: pointer;
  color: #fbbf24;
  filter: drop-shadow(0 0 8px rgba(251, 191, 36, 0.6));
  transition: all 0.3s ease;
  animation: starGlow 2s ease-in-out infinite;
}

.preview-icon:hover {
  transform: scale(1.2);
  filter: drop-shadow(0 0 14px rgba(251, 191, 36, 0.9));
}

.preview-icon svg {
  display: block;
}

@keyframes starGlow {
  0%, 100% {
    filter: drop-shadow(0 0 6px rgba(251, 191, 36, 0.5));
    transform: scale(1);
  }
  50% {
    filter: drop-shadow(0 0 14px rgba(251, 191, 36, 0.8));
    transform: scale(1.08);
  }
}

.preview-icon .star-sm {
  opacity: 0.7;
}

.preview-icon .star-s1 {
  animation: twinkle 1.8s ease-in-out infinite;
}

.preview-icon .star-s2 {
  animation: twinkle 2.2s ease-in-out 0.4s infinite;
}

.preview-icon .star-s3 {
  animation: twinkle 2s ease-in-out 0.8s infinite;
}

.preview-icon .star-s4 {
  animation: twinkle 1.6s ease-in-out 1.2s infinite;
}

@keyframes twinkle {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}

/* 新手引导箭头 */
.preview-icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.guide-arrow {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  color: #fbbf24;
  margin-top: 4px;
  animation: guideBounce 1.5s ease-in-out infinite;
  pointer-events: none;
  filter: drop-shadow(0 0 6px rgba(251, 191, 36, 0.5));
}

.guide-text {
  font-size: 12px;
  color: #fbbf24;
  white-space: nowrap;
  margin-top: 2px;
  letter-spacing: 1px;
}

@keyframes guideBounce {
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(6px); }
}

/* 效果预览弹窗 */
.preview-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(8px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
}

.preview-dialog {
  background: #1e293b;
  border: 1px solid rgba(94, 234, 212, 0.2);
  border-radius: 16px;
  width: 90%;
  max-width: 1000px;
  max-height: 80vh;
  overflow: hidden;
  padding: 2rem;
  position: relative;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  animation: modalFadeIn 0.3s ease;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.preview-dialog-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(94, 234, 212, 0.1);
  color: #5eead4;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.3s;
}

.preview-dialog-close:hover {
  background: rgba(94, 234, 212, 0.2);
  transform: scale(1.1);
}

.preview-dialog-title {
  font-size: 1.4rem;
  color: #5eead4;
  margin-bottom: 1.5rem;
  padding-right: 40px;
}

.preview-dialog-body {
  display: flex;
  gap: 16px;
  overflow-y: auto;
  max-height: calc(80vh - 8rem);
}

.preview-dialog-side {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-dialog-side img {
  width: 100%;
  max-height: 60vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

@media (max-width: 700px) {
  .preview-dialog-body {
    flex-direction: column;
    gap: 12px;
  }
  .preview-dialog {
    width: 95%;
    padding: 1.5rem;
    margin: 10px;
  }
  .preview-dialog-title {
    font-size: 1.2rem;
  }
}

/* 确保容器正确布局 */
.container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* 上传结果弹窗 */
:global(.batch-upload-result-dialog) {
  border-radius: 12px !important;
}

:global(.batch-upload-result-dialog .el-message-box__header) {
  padding: 18px 20px 12px !important;
}

:global(.batch-upload-result-dialog .el-message-box__title) {
  font-size: 16px !important;
  font-weight: 600 !important;
}

:global(.batch-upload-result-dialog .el-message-box__status) {
  display: none !important;
}

:global(.batch-upload-result-dialog .el-message-box__content) {
  max-height: 400px !important;
  overflow-y: auto !important;
  padding: 0 20px 12px !important;
}

:global(.batch-upload-result-dialog .el-message-box__message) {
  padding: 0 !important;
}

:global(.batch-upload-result-dialog .el-message-box__btns) {
  padding: 8px 20px 18px !important;
}

:global(.upload-error-list) {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

:global(.upload-error-item) {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.5;
  color: #94a3b8;
  background: rgba(94, 234, 212, 0.08);
  border: 1px solid rgba(94, 234, 212, 0.12);
  transition: all 0.15s ease;
  word-break: break-all;
}

:global(.upload-error-item:hover) {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.2);
  color: #fca5a5;
}

.config-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.config-label {
  color: #e2e8f0;
  font-weight: 500;
  font-size: 14px;
}

.config-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.config-footer .el-button--primary {
  background: linear-gradient(90deg, #5eead4, #3b82f6) !important;
  color: #0f172a !important;
  border: none !important;
  font-weight: 600 !important;
}
</style>

<style>
.config-select-popper {
  z-index: 10001 !important;
}
</style>
